import { Divider, FlyerCard, HeaderFixed, StickyHeader } from '@/components';
import { GET_ORG } from '@/graphql/queries';
import { GetOrgResponse } from '@/graphql/types';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import scrollToTop from '@/utils/scroolToTop';
import { useQuery } from '@apollo/client';
import { cx } from '@emotion/css';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import PublicIcon from '@mui/icons-material/Public';
import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './OrgProfile.styles';
import OrgProfileDrawer from './components/OrgProfileDrawer/OrgProfileDrawer';
import OrgProfileHeader from './components/OrgProfileHeader/OrgProfileHeader';
import {
  BodyMedium,
  BodyMediumMidEmphasisUnderline,
  BodySmallMidEmphasisUnderline,
  Tag,
  TitleSmallEmphasized,
} from '@/design-system';
import { FollowButton } from './components/FollowButton/FollowButton';
import colors from '@/theme/colors';

const STICK_HEADER_HEIGHT = 163;

export const OrgProfile: React.FC = () => {
  const { id } = useParams();
  const { data } = useQuery<GetOrgResponse>(GET_ORG, {
    skip: !id,
    variables: {
      orgId: Number(id),
    },
  });
  const [showOptions, setShowOptions] = useState(false);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(STICK_HEADER_HEIGHT);
  const [stickyTop, setStickyTop] = useState(STICK_HEADER_HEIGHT);
  const { track } = useAnalytics();
  const trackId = 'org_profile_page';

  const org = data?.org;

  useEffect(() => {
    scrollToTop();
  }, []);

  if (!org) return null;

  const isSingleFlyer = org.currentProgram.length === 1;

  const toggleDrawer = (open: boolean) => (event: any) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:show_options`,
      label: org.name,
      value: Number(org.orgId),
    });
    setShowOptions(open);
  };

  const trackButtonClick = (label: string) => {
    track({
      category: trackId,
      action: GenericTrackTypes.BUTTON_PRESSED,
      label,
    });
  };

  const onStickyTopChanged = (stickyTop: number) => {
    setStickyTop(stickyTop);
  };

  return (
    <>
      <HeaderFixed>
        <OrgProfileHeader
          image={org.currentProgram.length > 0 ? org.currentProgram[0].frontImageUrl : ''}
          trackButtonClick={trackButtonClick}
          toggleDrawer={toggleDrawer}
        />
      </HeaderFixed>
      <main>
        <StickyHeader
          stickyHeaderHeight={stickyHeaderHeight}
          onStickyTopChanged={onStickyTopChanged}
        >
          <div className={styles.section}>
            <div className={styles.orgNameTitle}>
              <TitleSmallEmphasized>{org?.name}</TitleSmallEmphasized>
              <FollowButton onClick={() => trackButtonClick('Follow')} />
            </div>
            <div className={styles.tagsContainer}>
              {org.yearEstablished && <Tag variant="timeline">Since {org.yearEstablished}</Tag>}
            </div>
            {org.childEnrichmentStatement && (
              <BodyMedium>{org.childEnrichmentStatement}</BodyMedium>
            )}
            <Divider className={styles.divider} />
          </div>
          <div className={cx(styles.section, styles.infoContainer)}>
            <div className={styles.info}>
              <PhoneInTalkOutlinedIcon />
              <a
                href={`tel:${org.links.call}`}
                className={styles.infoText}
                aria-label="organization phone"
                onClick={() => {
                  trackButtonClick('Call');
                }}
              >
                <BodyMedium>{org.links.call}</BodyMedium>
              </a>
            </div>
            <div className={styles.info}>
              <EmailOutlinedIcon />
              <a
                href={`mailto:${org.links.email}`}
                className={styles.infoText}
                aria-label="organization email"
                onClick={() => {
                  trackButtonClick('Email');
                }}
              >
                <BodyMediumMidEmphasisUnderline color={colors.jungle}>
                  {org.links.email}
                </BodyMediumMidEmphasisUnderline>
              </a>
            </div>
            {org.links.website && (
              <div className={styles.info}>
                <PublicIcon />
                <a
                  href={`${org.links.website}`}
                  className={styles.infoText}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="organization website"
                  onClick={() => {
                    trackButtonClick('Website');
                  }}
                >
                  <BodyMediumMidEmphasisUnderline color={colors.jungle}>
                    {org.links.website}
                  </BodyMediumMidEmphasisUnderline>
                </a>
              </div>
            )}
            <div className={styles.info}>
              <LocationOnOutlinedIcon />
              <BodyMedium className={styles.infoText}>
                {org.address} <br />
                <a
                  href={`http://maps.google.com/?daddr=${org.mapUrl}`}
                  target="_blank"
                  className={styles.link}
                  rel="noreferrer"
                  aria-label="organization address"
                  onClick={() => {
                    trackButtonClick('Address');
                  }}
                >
                  <BodySmallMidEmphasisUnderline color={colors.jungle}>
                    View Location
                  </BodySmallMidEmphasisUnderline>
                </a>
              </BodyMedium>
            </div>
            {org?.socialMediaUrl && (
              <div className={styles.info}>
                <ShareOutlinedIcon />
                <a
                  href={`${org.socialMediaUrl}`}
                  className={styles.infoText}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="organization social media"
                  onClick={() => {
                    trackButtonClick('Social Media');
                  }}
                >
                  <BodyMediumMidEmphasisUnderline color={colors.jungle}>
                    {org.socialMediaUrl}
                  </BodyMediumMidEmphasisUnderline>
                </a>
              </div>
            )}
            {org.seasonality?.length > 0 && (
              <div className={styles.info}>
                <BeachAccessOutlinedIcon />
                <BodyMedium className={styles.infoText}>{org.seasonality.join(', ')}</BodyMedium>
              </div>
            )}
          </div>
          {org.currentProgram.length > 0 && (
            <>
              <div className={styles.flyerSection} />
              <div className={styles.section}>
                <TitleSmallEmphasized
                  className={styles.sectionTitle}
                  style={{ top: `${stickyTop}px` }}
                >
                  Current Flyers
                </TitleSmallEmphasized>
                <Grid container spacing={2} className={styles.flyersContainer}>
                  {org.currentProgram.map(
                    ({ flyerId, title, frontImageUrl, distributionsReleased }) => (
                      <Grid item xs={isSingleFlyer ? 12 : 6} key={flyerId}>
                        <FlyerCard
                          id={flyerId}
                          title={title}
                          thumbnail={frontImageUrl}
                          isFavorite={false}
                          trackId={`${trackId}:current_program`}
                          trackingEventFrom=""
                          distributionsReleased={distributionsReleased}
                        />
                      </Grid>
                    )
                  )}
                </Grid>
              </div>
            </>
          )}

          {org.previouslyPosted.length > 0 && (
            <>
              <div className={styles.flyerSection} />
              <div className={styles.section}>
                <TitleSmallEmphasized
                  className={styles.sectionTitle}
                  style={{ top: `${stickyTop}px` }}
                >
                  Expired Flyers
                </TitleSmallEmphasized>
                <Grid container spacing={2} className={styles.flyersContainer}>
                  {org.previouslyPosted?.map(
                    ({ flyerId, frontImageUrl, title, distributionsReleased }) => (
                      <Grid item xs={6} key={flyerId}>
                        <FlyerCard
                          id={flyerId}
                          title={title}
                          thumbnail={frontImageUrl}
                          isFavorite={false}
                          trackId={`${trackId}:previouslyPosted`}
                          inactive
                          trackingEventFrom=""
                          distributionsReleased={distributionsReleased}
                        />
                      </Grid>
                    )
                  )}
                </Grid>
              </div>
            </>
          )}
        </StickyHeader>
      </main>
      <OrgProfileDrawer setShowOptions={setShowOptions} showOptions={showOptions} />
    </>
  );
};
